import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { navigate } from '@reach/router'
import { Button, Icon, Container, Section } from 'chaoskit/src/components'
import { InstantSearch, Configure } from 'react-instantsearch-dom'
import { misc } from 'chaoskit/src/assets/styles/utility'

import { getAlgoliaIndexName } from '../../../utils/helpers'

import BaseLayout from '~layouts/BaseLayout'
import Hero, {
  HeroContent,
  HeroTitleWrapper,
  HeroTitle,
  HeroTitleSub,
} from '~partials/Hero'
import {
  createUrl,
  searchStateToUrl,
  urlToSearchState,
} from '~partials/search/utility'
import {
  CustomSearchBox,
  CustomClearRefinements,
  CustomInfiniteHits,
  DEBOUNCE_TIME,
} from '~partials/search/algolia-global'
import {
  CustomAuthorMenu,
  CustomCategoryMenu,
  Hit,
} from '~partials/search/algolia-tutorials'
import HelmetSEO from '~partials/global/HelmetSEO'
import useAlgoliaSearchClient from '~hooks/useAlgoliaSearchClient'
import { stickyIt } from '~styles/utility'
import { StylesHeaderVariables } from '~partials/header/UI'

const { algoliaIndex } = getAlgoliaIndexName()

const Tutorials = ({ location }) => {
  const theme = useTheme()

  const searchClient = useAlgoliaSearchClient((state) => state.searchClient)
  const [isFilterOpen, setFilterOpen] = useState(false)
  const [searchState, setSearchState] = useState(urlToSearchState(location))
  const [debouncedSetState, setDebouncedSetState] = useState(null)

  const handleSearchStateChange = (updatedSearchState) => {
    clearTimeout(debouncedSetState)

    setDebouncedSetState(
      setTimeout(() => {
        navigate(searchStateToUrl(updatedSearchState), updatedSearchState)
      }, DEBOUNCE_TIME)
    )

    setSearchState(updatedSearchState)
  }

  // Make sure we close mobile nav if resizing from mobile to desktop
  const checkSize = (event) => {
    if (event.matches) {
      setFilterOpen(true)
    } else {
      setFilterOpen(false)
    }
  }

  useEffect(() => {
    setSearchState(urlToSearchState(location))
  }, [location])

  useEffect(() => {
    // Always scroll to top on mount
    window.scrollTo(0, 0)

    const viewportMediumMin = window.matchMedia(
      `(min-width: ${theme.breakpoint.medium}px)`
    )

    // Check on-load
    checkSize(viewportMediumMin)

    // Add listener
    viewportMediumMin.addListener(checkSize)

    return () => {
      // Clean-up
      viewportMediumMin.removeListener(checkSize)
    }
  }, [theme.breakpoint.medium])

  return (
    <BaseLayout>
      <HelmetSEO title="Tutorials" />
      <InstantSearch
        indexName={algoliaIndex}
        searchClient={searchClient}
        searchState={searchState}
        onSearchStateChange={handleSearchStateChange}
        createURL={createUrl}
      >
        <Configure
          filters="articleLabel:Tutorials"
          facetingAfterDistinct
          hitsPerPage="20"
        />
        <Hero
          compact
          css={{
            background: theme.brand.darkViolet,
          }}
        >
          <HeroContent>
            <Container>
              <HeroTitleWrapper bottomSpace>
                <HeroTitle>Tutorials</HeroTitle>
                <HeroTitleSub>
                  <p>
                    Gremlin provides guides and walkthroughs to help you become
                    a reliability expert.
                  </p>
                </HeroTitleSub>
              </HeroTitleWrapper>
              <div
                css={{
                  display: 'grid',
                  justifyContent: 'center',
                  gridTemplateColumns: '1fr',

                  [theme.mq.medium]: { gridTemplateColumns: '0.75fr' },
                  [theme.mq.large]: { gridTemplateColumns: '0.6fr' },
                }}
              >
                <CustomSearchBox
                  label="Search tutorials"
                  placeholderText="Reliability Management"
                />
              </div>
            </Container>
          </HeroContent>
        </Hero>
        <Section>
          <Container>
            <div
              css={{
                display: 'grid',
                gap: theme.space.large,

                [theme.mq.medium]: { gridTemplateColumns: '250px 1fr' },
              }}
            >
              <div>
                <div
                  css={[
                    {
                      textAlign: 'center',

                      [theme.mq.medium]: {
                        display: 'none',
                      },
                    },

                    isFilterOpen && {
                      marginBottom: theme.space.base,
                    },
                  ]}
                >
                  <Button
                    type="default"
                    size="small"
                    onClick={() => setFilterOpen(!isFilterOpen)}
                  >
                    Filter{' '}
                    <Icon
                      css={{ marginLeft: theme.space.xsmall }}
                      icon={isFilterOpen ? 'caret-down' : 'caret-right'}
                      size="small"
                    />
                  </Button>
                </div>

                <div
                  css={[
                    !isFilterOpen && misc.hide,
                    {
                      [theme.mq.medium]: {
                        ...stickyIt(theme),
                        overflow: 'auto',
                        maxHeight: `calc(100vh - ${
                          StylesHeaderVariables(theme).height + theme.space.base
                        }px)`,
                      },
                    },
                  ]}
                >
                  <h4>Authors</h4>
                  <CustomAuthorMenu attribute="author" />
                  <h4>Categories</h4>
                  <CustomCategoryMenu attribute="categories" />
                  <CustomClearRefinements
                    searchUrl={searchStateToUrl(location, searchState)}
                  />
                </div>
              </div>
              <div>
                <CustomInfiniteHits
                  hitTemplate={Hit}
                  customCss={{
                    [theme.mq.medium]: {
                      gridTemplateColumns: 'repeat(2, 1fr)',
                    },
                  }}
                />
              </div>
            </div>
          </Container>
        </Section>
      </InstantSearch>
    </BaseLayout>
  )
}

Tutorials.propTypes = {
  location: PropTypes.object.isRequired,
}

export default Tutorials
